if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.defaultAddToCartText =
          this.submitButton?.dataset?.defaultLabel || window.variantStrings.addToCart;
        this.sectionId = this.dataset.sectionId;
        this.kitPersonalization = this.querySelector('[data-kit-personalization]');
        this.kitPersonalizationError = this.kitPersonalization?.querySelector('[data-kit-personalization-error]');
        this.kitPersonalizationStatus = this.kitPersonalization?.querySelector('[data-kit-personalization-status]');
        this.kitPersonalizationFields = this.kitPersonalization?.querySelector('[data-kit-personalization-fields]');
        this.kitPersonalizationReady = !this.kitPersonalization;
        this.kitVariantTitle = this.kitPersonalization?.dataset.initialVariantTitle;
        this.variantUnavailable = this.submitButton.disabled;

        if (this.kitPersonalization) {
          try {
            this.kitPersonalizationConfig = JSON.parse(this.kitPersonalization.dataset.kitPersonalizationConfig);
            this.validateKitPersonalizationConfig();
            this.kitPersonalizationReady = true;
          } catch (error) {
            console.error('Configuração de personalização do kit inválida:', error);
            this.showKitPersonalizationError();
          }
        }

        this.selectionGroups = [
          this.createSelectionGroup({
            fieldsetId: `signos-fieldset-${this.sectionId}`,
            errorId: `signos-error-${this.sectionId}`,
            hiddenInputId: `signos-value-${this.sectionId}`,
            statusId: `signos-status-${this.sectionId}`,
            countId: `signos-count-${this.sectionId}`,
            checkboxClass: 'signo-cb',
            propertyName: 'Signos escolhidos',
            maxSelections: 4,
            singularLabel: 'signo',
            pluralLabel: 'signos',
            errorMessage: 'Selecione exatamente 4 signos para continuar.',
            selectionPrompt: 'Selecione 4 signos para habilitar o botão de compra.',
            validMessage: 'Perfeito! Pode clicar em Adicionar ao carrinho.',
          }),
          this.createSelectionGroup({
            fieldsetId: `cores-fieldset-${this.sectionId}`,
            errorId: `cores-error-${this.sectionId}`,
            hiddenInputId: `cores-value-${this.sectionId}`,
            statusId: `cores-status-${this.sectionId}`,
            countId: `cores-count-${this.sectionId}`,
            checkboxClass: 'cor-cb',
            propertyName: 'Cores escolhidas',
            maxSelections: 2,
            singularLabel: 'cor',
            pluralLabel: 'cores',
            errorMessage: 'Selecione exatamente 2 cores para continuar.',
            selectionPrompt: 'Selecione 2 cores para habilitar o botão de compra.',
            validMessage: 'Perfeito! Pode clicar em Adicionar ao carrinho.',
          }),
          this.createSelectionGroup({
            fieldsetId: `cores-cogumelo-fieldset-${this.sectionId}`,
            errorId: `cores-cogumelo-error-${this.sectionId}`,
            hiddenInputId: `cores-cogumelo-value-${this.sectionId}`,
            statusId: `cores-cogumelo-status-${this.sectionId}`,
            countId: `cores-cogumelo-count-${this.sectionId}`,
            checkboxClass: 'cor-cogumelo-cb',
            propertyName: 'Cores escolhidas',
            maxSelections: 2,
            singularLabel: 'cor',
            pluralLabel: 'cores',
            errorMessage: 'Selecione exatamente 2 cores para continuar.',
            selectionPrompt: 'Selecione 2 cores para habilitar o botão de compra.',
            validMessage: 'Perfeito! Pode clicar em Adicionar ao carrinho.',
          }),
        ].filter(Boolean);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initializeKitPersonalization();
        this.initSelectionGroups();
        this.updateSelectionSubmitState();
      }

      validateKitPersonalizationConfig() {
        const config = this.kitPersonalizationConfig;
        const hasGroups = Array.isArray(config?.groups);
        const hasVariants = config?.variants && typeof config.variants === 'object';
        if (!hasGroups && !hasVariants) {
          throw new Error('A configuração precisa incluir "groups" ou "variants".');
        }
      }

      initializeKitPersonalization() {
        if (!this.kitPersonalization) return;

        this.kitPersonalization.addEventListener('change', () => {
          this.updateKitPersonalizationState();
          this.updateSelectionSubmitState();
        });

        this.renderKitPersonalization(this.kitVariantTitle);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;

          this.variantUnavailable = this.submitButton.disabled;
          this.kitVariantTitle = event.data.variant?.title;
          this.renderKitPersonalization(this.kitVariantTitle);
          this.updateSelectionSubmitState();
        });
      }

      renderKitPersonalization(variantTitle) {
        if (!this.kitPersonalization || !this.kitPersonalizationReady) return;

        const groups = this.kitPersonalizationConfig.variants?.[variantTitle] ?? this.kitPersonalizationConfig.groups;
        if (!Array.isArray(groups) || groups.length === 0) {
          console.error(`Não há configuração de personalização para a variante "${variantTitle}".`);
          this.showKitPersonalizationError();
          this.updateSelectionSubmitState();
          return;
        }

        const previousSelections = new Map(
          Array.from(this.kitPersonalizationFields.querySelectorAll('select[data-kit-personalization-field]')).map(
            (select) => [select.name, select.value]
          )
        );
        this.kitPersonalizationFields.replaceChildren();
        this.kitPersonalizationError.hidden = true;

        for (const group of groups) {
          const quantity = Number(group.quantity);
          const options = Array.isArray(group.options) ? group.options : this.kitPersonalizationConfig.options;
          if (
            typeof group.name !== 'string' ||
            !group.name.trim() ||
            !Number.isInteger(quantity) ||
            quantity < 1 ||
            quantity > 50 ||
            !Array.isArray(options) ||
            options.length === 0 ||
            options.some((option) => typeof option !== 'string' || !option.trim())
          ) {
            console.error('Grupo inválido na configuração de personalização do kit:', group);
            this.showKitPersonalizationError();
            this.updateSelectionSubmitState();
            return;
          }

          const groupElement = document.createElement('div');
          groupElement.className = 'kit-personalization__group';
          const groupTitle = document.createElement('p');
          groupTitle.className = 'kit-personalization__group-title';
          groupTitle.textContent = group.name.trim();
          groupElement.append(groupTitle);
          this.kitPersonalizationFields.append(groupElement);

          for (let index = 1; index <= quantity; index += 1) {
            const propertyName = `${group.name.trim()} ${index}`;
            const label = document.createElement('label');
            label.className = 'kit-personalization__field';

            const labelText = document.createElement('span');
            labelText.textContent = `${index}º`;
            label.setAttribute('aria-label', propertyName);

            const selectWrapper = document.createElement('div');
            selectWrapper.className = 'select';

            const select = document.createElement('select');
            select.className = 'select__select';
            select.name = `properties[${propertyName}]`;
            select.required = true;
            select.dataset.kitPersonalizationField = '';

            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Escolha';
            select.append(placeholder);

            options.forEach((value) => {
              const option = document.createElement('option');
              option.value = value;
              option.textContent = value;
              select.append(option);
            });

            const caretWrapper = document.createElement('span');
            caretWrapper.className = 'svg-wrapper';
            caretWrapper.setAttribute('aria-hidden', 'true');
            caretWrapper.innerHTML =
              '<svg viewBox="0 0 10 6" focusable="false" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';

            if (previousSelections.has(select.name)) {
              select.value = previousSelections.get(select.name);
            }

            selectWrapper.append(select);
            selectWrapper.append(caretWrapper);
            label.append(labelText, selectWrapper);
            groupElement.append(label);
          }
        }

        this.updateKitPersonalizationState();
      }

      updateKitPersonalizationState() {
        if (!this.kitPersonalization || !this.kitPersonalizationReady) return false;

        const selects = Array.from(
          this.kitPersonalizationFields.querySelectorAll('select[data-kit-personalization-field]')
        );
        const selectedCount = selects.filter((select) => select.value !== '').length;
        const isValid = selects.length > 0 && selectedCount === selects.length;

        this.kitPersonalizationStatus.textContent = `Selecione uma opção para cada peça (${selectedCount}/${selects.length}).`;
        this.kitPersonalizationStatus.classList.toggle('kit-personalization__status--complete', isValid);
        this.kitPersonalizationError.hidden = true;
        return isValid;
      }

      showKitPersonalizationError() {
        if (!this.kitPersonalization) return;

        this.kitPersonalizationReady = false;
        this.kitPersonalizationError.hidden = false;
        this.kitPersonalizationStatus.textContent = '';
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      createSelectionGroup(config) {
        const fieldset = document.getElementById(config.fieldsetId);
        if (!fieldset) return null;

        return {
          ...config,
          fieldset,
          errorElement: document.getElementById(config.errorId),
          hiddenInput: document.getElementById(config.hiddenInputId),
          statusElement: document.getElementById(config.statusId),
          countElement: document.getElementById(config.countId),
        };
      }

      initSelectionGroups() {
        if (!this.selectionGroups.length || this.selectionGroupsInitialized) return;

        this.selectionGroupsInitialized = true;

        this.selectionGroups.forEach((group) => {
          group.fieldset.addEventListener('change', (event) => {
            const target = event.target;
            if (!target.classList || !target.classList.contains(group.checkboxClass)) return;

            const selectedValues = this.getSelectedValues(group);
            if (selectedValues.length > group.maxSelections) {
              target.checked = false;
            }

            this.syncSelectionGroup(group);
            this.updateSelectionGroupState(group);
            this.updateSelectionSubmitState();
          });
        });

        this.syncAllSelectionGroups();
        this.updateSelectionSubmitState();
      }

      getSelectedValues(group) {
        if (!group?.fieldset) return [];

        return Array.from(group.fieldset.querySelectorAll(`.${group.checkboxClass}:checked`)).map((input) => input.value);
      }

      syncSelectionGroup(group) {
        if (group?.hiddenInput) {
          group.hiddenInput.value = this.getSelectedValues(group).join(', ');
        }
      }

      syncAllSelectionGroups() {
        this.selectionGroups.forEach((group) => this.syncSelectionGroup(group));
      }

      toggleSelectionError(group, show) {
        if (group?.errorElement) {
          group.errorElement.style.display = show ? 'block' : 'none';
        }
      }

      isSelectionValid(group) {
        return this.getSelectedValues(group).length === group.maxSelections;
      }

      updateSelectionGroupState(group) {
        if (!group?.fieldset) return;

        const selectedCount = this.getSelectedValues(group).length;
        const isValid = selectedCount === group.maxSelections;

        if (group.countElement) {
          group.countElement.textContent = `${selectedCount}/${group.maxSelections}`;
          group.countElement.style.background = isValid ? '#e8f7eb' : '#f1f1f1';
          group.countElement.style.color = isValid ? '#1d6b34' : '#333';
        }

        if (group.statusElement) {
          if (isValid) {
            group.statusElement.textContent = group.validMessage;
            group.statusElement.style.color = '#1d6b34';
          } else if (selectedCount === 0) {
            group.statusElement.textContent = group.selectionPrompt;
            group.statusElement.style.color = '#333';
          } else {
            const missingCount = group.maxSelections - selectedCount;
            group.statusElement.textContent = `Selecione mais ${missingCount} ${missingCount > 1 ? group.pluralLabel : group.singularLabel} para habilitar o botão de compra.`;
            group.statusElement.style.color = '#333';
          }
        }

        if (!isValid) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      updateSelectionSubmitState() {
        if (!this.selectionGroups.length && !this.kitPersonalization) return;

        const selectionsAreValid = this.selectionGroups.every((group) => this.isSelectionValid(group));
        const personalizationIsValid =
          !this.kitPersonalization || this.updateKitPersonalizationState();
        const isValid = selectionsAreValid && personalizationIsValid && !this.variantUnavailable;
        this.submitButton.toggleAttribute('disabled', !isValid);

        if (isValid) {
          this.submitButton.removeAttribute('aria-disabled');
          this.handleErrorMessage();
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
        }

        this.selectionGroups.forEach((group) => this.updateSelectionGroupState(group));
      }

      validateSelectionGroups() {
        if (this.kitPersonalization && !this.updateKitPersonalizationState()) {
          this.kitPersonalizationError.hidden = false;
          this.kitPersonalization.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        if (!this.selectionGroups.length) return true;

        const invalidGroup = this.selectionGroups.find((group) => !this.isSelectionValid(group));
        if (invalidGroup) {
          this.toggleSelectionError(invalidGroup, true);
          this.handleErrorMessage(invalidGroup.errorMessage);
          invalidGroup.fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.updateSelectionSubmitState();
          return false;
        }

        this.selectionGroups.forEach((group) => this.toggleSelectionError(group, false));
        this.handleErrorMessage();
        this.syncAllSelectionGroups();
        this.updateSelectionSubmitState();
        return true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.validateSelectionGroups()) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.selectionGroups.forEach((group) => {
          const selectedValues = this.getSelectedValues(group);
          if (selectedValues.length) {
            formData.set(`properties[${group.propertyName}]`, selectedValues.join(', '));
          }
        });
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.defaultAddToCartText;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
